import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { ButtonIcon } from "../../molecules/buttonIcon/ButtonIcon";
import { style as modalStyle } from "./Modal.style";
import "./Modal.web.css";
const TRANSITION_DELAY = 200;
const TRANSITION_MODAL_SCALE = 0.9;
const columnSizeWidths = {
    "1/4": `${100 * (1 / 4)}%`,
    "1/3": `${100 * (1 / 3)}%`,
    "1/2": `${100 * (1 / 2)}%`,
    "2/3": `${100 * (2 / 3)}%`,
    "1": "100%",
};
/* eslint-enable @typescript-eslint/naming-convention */
const Modal = ({ children, visible, fullscreen = false, header, footer, onClose, showCloseButton = false, size = { M: "2/3", L: "1/3" }, style: customStyle = {}, scroll = false, testID = "modal", scrollRef, }) => {
    const style = useMemo(() => modalStyle(), []);
    const screenSize = useScreenSize();
    const isSmallDevice = screenSize === "S";
    const columnSizeForScreenSize = size[screenSize];
    const headerId = useId();
    const containerRef = useRef(null);
    const [mounted, setMounted] = useState(visible);
    const [animating, setAnimating] = useState(visible);
    const previousFocusRef = useRef(null);
    const closeButtonStyle = useMemo(() => ({ button: [style.closeButton, customStyle?.closeButton] }), [customStyle?.closeButton, style.closeButton]);
    const opacityNotVisible = isSmallDevice ? 1 : 0;
    const scaleNotVisible = isSmallDevice ? 1 : TRANSITION_MODAL_SCALE;
    useEffect(() => {
        if (visible) {
            setMounted(true);
            const timer = setTimeout(() => setAnimating(true), TRANSITION_DELAY);
            previousFocusRef.current = document.activeElement;
            return () => clearTimeout(timer);
        }
        else {
            setAnimating(false);
            const timer = setTimeout(() => setMounted(false), 300);
            return () => clearTimeout(timer);
        }
    }, [visible]);
    useEffect(() => {
        if (!mounted || !containerRef.current)
            return;
        const container = containerRef.current;
        const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const getFocusable = () => Array.from(container.querySelectorAll(focusableSelectors));
        const focusFirst = () => {
            const focusable = getFocusable();
            if (focusable.length)
                focusable[0].focus();
            else
                container.focus();
        };
        focusFirst();
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
                return;
            }
            if (e.key !== "Tab")
                return;
            const focusable = getFocusable();
            if (!focusable.length)
                return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            }
            else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        const observer = new MutationObserver(() => {
            const focusable = getFocusable();
            if (focusable.length && !container.contains(document.activeElement)) {
                focusable[0].focus();
            }
        });
        observer.observe(container, { childList: true, subtree: true });
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            observer.disconnect();
            document.removeEventListener("keydown", handleKeyDown);
            previousFocusRef.current?.focus();
        };
    }, [mounted, onClose]);
    if (!mounted)
        return null;
    const modalMaxWidth = fullscreen || isSmallDevice
        ? "100%"
        : columnSizeForScreenSize
            ? columnSizeWidths[columnSizeForScreenSize]
            : undefined;
    const modalMaxHeight = fullscreen
        ? "100%"
        : `calc(100vh - ${isSmallDevice ? 0 : style.modalDesktopVerticalPadding}px)`;
    const modalTransform = animating
        ? "translateY(0px) scale(1)"
        : isSmallDevice
            ? "translateY(100%)"
            : `translateY(20px) scale(${scaleNotVisible})`;
    const borderRadius = fullscreen ? 0 : style.modal.borderRadius;
    const bottomRadius = isSmallDevice && !fullscreen ? 0 : borderRadius;
    return createPortal(React.createElement(React.Fragment, null,
        React.createElement("div", { className: "modal-overlay", "data-testid": "modal-overlay", style: {
                backgroundColor: style.overlay.backgroundColor,
                opacity: animating ? 1 : 0,
                transition: animating ? "opacity 300ms ease" : `opacity 300ms ease ${TRANSITION_DELAY}ms`,
                zIndex: (style.container.zIndex ?? 100) - 1,
                ...customStyle?.overlay,
            }, onClick: onClose }),
        React.createElement("div", { className: "modal-centering", style: {
                alignItems: fullscreen ? "stretch" : isSmallDevice ? "flex-end" : "center",
                left: "50%",
                marginLeft: "auto",
                marginRight: "auto",
                maxWidth: isSmallDevice || fullscreen ? "100%" : style.layoutMaxWidth,
                right: "auto",
                transform: "translateX(-50%)",
                width: "100%",
                zIndex: style.container.zIndex,
                ...customStyle?.row,
            } },
            React.createElement("div", { ref: containerRef, "aria-labelledby": header ? headerId : undefined, "aria-modal": "true", className: "modal-dialog", "data-testid": testID, role: "dialog", tabIndex: -1, style: {
                    backgroundColor: style.modal.backgroundColor,
                    borderRadius,
                    borderBottomLeftRadius: bottomRadius,
                    borderBottomRightRadius: bottomRadius,
                    height: fullscreen ? "100%" : undefined,
                    maxHeight: modalMaxHeight,
                    maxWidth: modalMaxWidth ?? "100%",
                    opacity: animating ? 1 : opacityNotVisible,
                    transform: modalTransform,
                    ...customStyle?.modal,
                } },
                React.createElement("div", { className: "modal-inner", style: {
                        borderRadius,
                        borderBottomLeftRadius: bottomRadius,
                        borderBottomRightRadius: bottomRadius,
                    } },
                    (header || showCloseButton) && (React.createElement("div", { className: "modal-header", style: {
                            padding: style.header.padding,
                            ...customStyle?.header,
                        } },
                        showCloseButton && React.createElement(ButtonIcon, { name: "close", style: closeButtonStyle, onPress: onClose }),
                        React.createElement("div", { id: header ? headerId : undefined }, header))),
                    scroll ? (React.createElement("div", { ref: scrollRef, className: "modal-scroll-content" },
                        React.createElement("div", { style: { paddingBottom: style.safeAreaInsetBottom } }, children))) : (React.createElement("div", { className: "modal-static-content" },
                        React.createElement("div", { style: { paddingBottom: style.safeAreaInsetBottom } }, children)))),
                footer && (React.createElement("div", { className: "modal-footer", style: {
                        backgroundColor: style.modal.backgroundColor,
                        paddingBottom: style.stickyFooter.paddingBottom,
                        paddingLeft: style.stickyFooter.paddingHorizontal,
                        paddingRight: style.stickyFooter.paddingHorizontal,
                        paddingTop: style.stickyFooter.paddingTop,
                    } }, footer))))), document.body);
};
export { Modal };
